import type {
    IIssueSuggestedQuestionsResponse,
    IssueActivityResponseUnion,
    IssueResponseBase,
} from '@kcftech/prometheus-client';
import { IssueStatusType } from '@kcftech/prometheus-client';
import { createContext, ReactNode, useContext, useEffect, useMemo, useRef } from 'react';
import type { RecurseNonSdkClass } from 'utils/sdk_types';

export type IssueEvent = Readonly<
    | {
          type: 'activity';
          action: 'create' | 'update' | 'delete';
          activityId: string;
          activity: IssueActivityResponseUnion;
      }
    | {
          type: 'activity-remove';
          activityId: string;
          issueId: string;
      }
    | {
          type: 'issue';
          action: 'create' | 'update' | 'view';
          issueId: string;
      }
    | {
          type: 'flag';
          action: 'update';
          issueId: string;
          flagInfo: {
              flagged: boolean;
              shouldReceiveNotifications: boolean;
              isArchived: boolean;
          };
      }
    | {
          type: 'task';
          action: 'create' | 'update' | 'delete';
          workItemId: string;
      }
    | {
          type: 'suggestedQuestions';
          response: IIssueSuggestedQuestionsResponse;
      }
>;

export const shouldCauseIssueRefetch = (event: IssueEvent): boolean => {
    return (
        event.type === 'issue' ||
        (event.type === 'activity' &&
            !(
                event.activity.discriminator === 'Comment' ||
                event.activity.discriminator === 'CommentDelete' ||
                event.activity.discriminator === 'CommentReply' ||
                event.activity.discriminator === 'ChartData'
            )) ||
        event.type === 'flag'
    );
};

export type IssuePatch = (iss: RecurseNonSdkClass<IssueResponseBase>) => void;

export function getIssuePatchForEvent(
    event: IssueEvent,
    currentStatusType: IssueStatusType,
): IssuePatch | undefined {
    if (event.type !== 'activity') {
        return undefined;
    }

    switch (event.activity.discriminator) {
        case 'Publish':
            if (currentStatusType !== IssueStatusType.Draft) {
                return undefined;
            }
            return (iss) => {
                iss.IssueStatus = {
                    StatusType: IssueStatusType.New,
                    StatusName: 'New',
                    Id: 'nil',
                };
            };
        case 'StatusUpdate': {
            const activity = event.activity;
            return (iss) => {
                iss.IssueStatus = {
                    StatusType: activity.ToStatusType,
                    StatusName: activity.ToStatusName,
                    Id: 'nil-' + activity.ToStatusType,
                };
            };
        }
        case 'PriorityUpdate': {
            const activity = event.activity;
            return (iss) => {
                iss.Priority = activity.ToPriority;
            };
        }
        case 'SeverityUpdate': {
            const activity = event.activity;
            return (iss) => {
                iss.Severity = activity.ToSeverity;
            };
        }
        case 'RecommendationUpdate': {
            const activity = event.activity;
            return (iss) => {
                iss.RecommendedAction = activity.ToRecommendation;
            };
        }
        case 'DescriptionUpdate': {
            const activity = event.activity;
            return (iss) => {
                iss.Description = activity.ToDescription;
            };
        }
        default:
            return undefined;
    }
}

export type IssueEventCallback = (event: IssueEvent) => void;

export type IssueEventsContext = Readonly<{
    registerListener: (callback: IssueEventCallback) => () => void;
    triggerIssueEvent: (event: IssueEvent) => void;
    destroy: () => void;
}>;

const DedupWindowMs = 10_000;

function getEventDedupeKey(event: IssueEvent): string | undefined {
    switch (event.type) {
        case 'activity':
            return `activity:${event.activityId}:${event.action}`;
        case 'task':
            return `task:${event.workItemId}:${event.action}`;
        default:
            return undefined;
    }
}

function buildIssueEventContext() {
    const callbacks = new Set<IssueEventCallback>();
    const recentEvents = new Set<string>();

    const contextValue: IssueEventsContext = {
        registerListener: (callback) => {
            callbacks.add(callback);

            return () => {
                callbacks.delete(callback);
            };
        },
        triggerIssueEvent: (event) => {
            // in order to avoid the same event potentially being emitted from
            // several sources (e.g. a local update followed by a signalr copy
            // of the same update), dedupe same events we've recently seen
            const key = getEventDedupeKey(event);
            if (key) {
                if (recentEvents.has(key)) {
                    return;
                }
                recentEvents.add(key);
                setTimeout(() => recentEvents.delete(key), DedupWindowMs);
            }
            // exec
            callbacks.forEach((callback) => callback(event));
        },
        destroy: () => {
            callbacks.clear();
            recentEvents.clear();
        },
    };

    return contextValue;
}

const Context = createContext<IssueEventsContext | undefined>(undefined);

export type IssueEventsProviderProps = Readonly<{ children?: ReactNode }>;

export function IssueEventsProvider({ children }: IssueEventsProviderProps) {
    const value: IssueEventsContext = useMemo(() => buildIssueEventContext(), []);

    useEffect(() => () => value.destroy(), [value]);

    return <Context.Provider value={value}>{children}</Context.Provider>;
}

function useIssueEventsContext() {
    const ctx = useContext(Context);

    if (!ctx) {
        throw new Error('Missing <IssueEventsProvider>');
    }

    return ctx;
}

export function useOnIssueEvent(callback: IssueEventCallback) {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    const { registerListener } = useIssueEventsContext();

    useEffect(() => {
        const unregister = registerListener((event) => callbackRef.current(event));
        return unregister;
    }, [registerListener]);
}

export function useTriggerIssueEvent() {
    return useIssueEventsContext().triggerIssueEvent;
}
